/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUserSessionsRequest,
  ListUserSessionsSecurity,
  QueryParamStatus,
} from "../models/operations/listusersessions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionsInfiniteQuery,
  buildUserSessionsQuery,
  prefetchUserSessions,
  prefetchUserSessionsInfinite,
  queryKeyUserSessions,
  queryKeyUserSessionsInfinite,
  UserSessionsInfiniteQueryData,
  UserSessionsPageParams,
  UserSessionsQueryData,
} from "./userSessions.core.js";
export {
  buildUserSessionsInfiniteQuery,
  buildUserSessionsQuery,
  prefetchUserSessions,
  prefetchUserSessionsInfinite,
  queryKeyUserSessions,
  queryKeyUserSessionsInfinite,
  type UserSessionsInfiniteQueryData,
  type UserSessionsPageParams,
  type UserSessionsQueryData,
};

export type UserSessionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUserSessions userSessions
 *
 * @remarks
 * List issued user_sessions in the caller's project. refresh_token_hash is never returned.
 */
export function useUserSessions(
  request?: ListUserSessionsRequest | undefined,
  security?: ListUserSessionsSecurity | undefined,
  options?: QueryHookOptions<UserSessionsQueryData, UserSessionsQueryError>,
): UseQueryResult<UserSessionsQueryData, UserSessionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessions userSessions
 *
 * @remarks
 * List issued user_sessions in the caller's project. refresh_token_hash is never returned.
 */
export function useUserSessionsSuspense(
  request?: ListUserSessionsRequest | undefined,
  security?: ListUserSessionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionsQueryData,
    UserSessionsQueryError
  >,
): UseSuspenseQueryResult<UserSessionsQueryData, UserSessionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessions userSessions
 *
 * @remarks
 * List issued user_sessions in the caller's project. refresh_token_hash is never returned.
 */
export function useUserSessionsInfinite(
  request?: ListUserSessionsRequest | undefined,
  security?: ListUserSessionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    UserSessionsInfiniteQueryData,
    UserSessionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<UserSessionsInfiniteQueryData, UserSessionsPageParams>,
  UserSessionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    UserSessionsInfiniteQueryData,
    UserSessionsQueryError,
    InfiniteData<UserSessionsInfiniteQueryData, UserSessionsPageParams>,
    QueryKey,
    UserSessionsPageParams
  >({
    ...buildUserSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listUserSessions userSessions
 *
 * @remarks
 * List issued user_sessions in the caller's project. refresh_token_hash is never returned.
 */
export function useUserSessionsInfiniteSuspense(
  request?: ListUserSessionsRequest | undefined,
  security?: ListUserSessionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    UserSessionsInfiniteQueryData,
    UserSessionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<UserSessionsInfiniteQueryData, UserSessionsPageParams>,
  UserSessionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    UserSessionsInfiniteQueryData,
    UserSessionsQueryError,
    InfiniteData<UserSessionsInfiniteQueryData, UserSessionsPageParams>,
    QueryKey,
    UserSessionsPageParams
  >({
    ...buildUserSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setUserSessionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      subjectUrn?: string | undefined;
      userSessionIssuerId?: string | undefined;
      status?: QueryParamStatus | undefined;
      clientId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionsQueryData,
): UserSessionsQueryData | undefined {
  const key = queryKeyUserSessions(...queryKeyBase);

  return client.setQueryData<UserSessionsQueryData>(key, data);
}

export function invalidateUserSessions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      subjectUrn?: string | undefined;
      userSessionIssuerId?: string | undefined;
      status?: QueryParamStatus | undefined;
      clientId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessions", "list", ...queryKeyBase],
  });
}

export function invalidateAllUserSessions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessions", "list"],
  });
}
